import { useState } from 'react'
import { useUsers } from '../hooks/useUsers'
import {
  ModalShell, Field, inputClass, CancelButton, PrimaryButton, DangerButton, ServerError
} from './modal-form'
import type { EventChecklistItem } from '../types'

// ChecklistItemEditor - one modal for both ad-hoc create and per-item edit.
// `item` null = create. Editing covers title, description, owner, due date,
// notes; checkbox-driven done state is handled inline on the tab (single
// click, no modal). Delete shows as a left-aligned oxblood action when in
// edit mode (existing item).

interface Props {
  item: EventChecklistItem | null
  defaultSection?: string
  saving?: boolean
  deleting?: boolean
  serverError?: Error | null
  onClose: () => void
  onSave: (body: {
    section: string
    title: string
    description: string | null
    ownerUserId: number | null
    dueDate: string | null
    notes: string | null
  }) => void
  onDelete?: () => void
}

export function ChecklistItemEditor({
  item, defaultSection, saving, deleting, serverError, onClose, onSave, onDelete
}: Props) {
  const isEditing = item != null
  const { data: users } = useUsers()

  const [section, setSection] = useState(item?.section ?? defaultSection ?? 'Pre-event')
  const [title, setTitle] = useState(item?.title ?? '')
  const [description, setDescription] = useState(item?.description ?? '')
  const [ownerUserId, setOwnerUserId] = useState<number | null>(item?.owner?.id ?? null)
  const [dueDate, setDueDate] = useState(item?.dueDate ?? '')
  const [notes, setNotes] = useState(item?.notes ?? '')

  const trimmedTitle = title.trim()
  const trimmedSection = section.trim()
  const isValid = trimmedTitle.length > 0 && trimmedSection.length > 0

  function handleSubmit() {
    if (!isValid) return
    onSave({
      section: trimmedSection,
      title: trimmedTitle,
      description: description.trim().length === 0 ? null : description,
      ownerUserId,
      dueDate: dueDate || null,
      notes: notes.trim().length === 0 ? null : notes
    })
  }

  const body = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-3">
        <Field label="Section">
          <input
            type="text"
            value={section}
            onChange={(e) => setSection(e.target.value)}
            placeholder="Pre-event"
            maxLength={80}
            className={inputClass(false)}
          />
        </Field>
        <Field label="Title">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Confirm visiting loco contract"
            maxLength={200}
            className={inputClass(!isValid && title.length > 0 && trimmedTitle.length === 0)}
          />
        </Field>
      </div>

      <Field label="Description (optional)">
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className={inputClass(false)}
        />
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Owner (optional)">
          <select
            value={ownerUserId ?? ''}
            onChange={(e) => setOwnerUserId(e.target.value ? Number(e.target.value) : null)}
            className={inputClass(false)}
          >
            <option value="">— Unassigned —</option>
            {(users ?? []).map((u) => (
              <option key={u.id} value={u.id}>{u.name ?? `User ${u.id}`}</option>
            ))}
          </select>
        </Field>
        <Field label="Due date (optional)">
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className={inputClass(false)}
          />
        </Field>
      </div>

      <Field label="Notes (optional)">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className={inputClass(false)}
        />
      </Field>

      {serverError && (
        <ServerError title="Could not save" error={serverError} />
      )}
    </div>
  )

  const footer = (
    <>
      {isEditing && onDelete && (
        <DangerButton onClick={onDelete} disabled={!!deleting}>
          {deleting ? 'Deleting…' : 'Delete'}
        </DangerButton>
      )}
      <CancelButton onClick={onClose} disabled={!!saving} />
      <PrimaryButton onClick={handleSubmit} disabled={!isValid || !!saving}>
        {saving ? 'Saving…' : isEditing ? 'Save' : 'Add item'}
      </PrimaryButton>
    </>
  )

  return (
    <ModalShell
      title={isEditing ? 'Edit checklist item' : 'Add checklist item'}
      onClose={onClose}
      body={body}
      footer={footer}
      onSubmit={(e) => {
        e.preventDefault()
        handleSubmit()
      }}
    />
  )
}
